import {GetSupportRoles, GetUsers} from "../../api/APISurvey";
import {createContext, useCallback, useContext, useMemo} from "react";
import {
    GetAssistantInstructionsConfig,
    GetBots,
    GetSupportbotInstructionsInstructions,
    GetSupportbotInstructionsSupportBotInstructionBody,
    GetSupportbotInstructionsSupportTopicInstructionBody,
    GetSupportTopics
} from "../../api/APIBot";

const SupportBotContext = createContext(null);

export function SupportBotProvider({ children }) {
    const supportBotData = useSupportBotData();

    return (
        <SupportBotContext.Provider value={supportBotData}>
            {children}
        </SupportBotContext.Provider>
    );
}

export function useSupportBotContext() {
    const context = useContext(SupportBotContext);
    if (!context) {
        throw new Error('useSupportBotContext must be used within a SupportBotProvider');
    }
    return context;
}

export function useSupportBotData() {
    const supportRoles = GetSupportRoles();
    const supportBots = GetBots();
    const supportTopicCodes = GetSupportTopics();
    const users = GetUsers();
    const assistantInstructionsConfig = GetAssistantInstructionsConfig()
    const supportBotInstructionsInstructions = GetSupportbotInstructionsInstructions()
    const supportBotInstructionsSupportBotInstructionBody = GetSupportbotInstructionsSupportBotInstructionBody()
    const supportBotInstructionsSupportTopicInstructionBody = GetSupportbotInstructionsSupportTopicInstructionBody()

    const isLoading = supportRoles.isLoading || supportBots.isLoading || users.isLoading || supportTopicCodes.isLoading || assistantInstructionsConfig.isLoading || supportBotInstructionsInstructions.isLoading || supportBotInstructionsSupportBotInstructionBody.isLoading || supportBotInstructionsSupportTopicInstructionBody.isLoading;
    const isError = supportRoles.isError || supportBots.isError || users.isError || supportTopicCodes.isError || assistantInstructionsConfig.isError || supportBotInstructionsInstructions.isError || supportBotInstructionsSupportBotInstructionBody.isError || supportBotInstructionsSupportTopicInstructionBody.isError;
    const isSuccess = supportRoles.isSuccess && supportBots.isSuccess && users.isSuccess && supportTopicCodes.isSuccess && assistantInstructionsConfig.isSuccess && supportBotInstructionsInstructions.isSuccess && supportBotInstructionsSupportBotInstructionBody.isSuccess && supportBotInstructionsSupportTopicInstructionBody.isSuccess;

    // Process bots data with versions and topic overrides
    const processedSupportBots = useMemo(() => {
        if (!supportBots.data || !supportTopicCodes.data?.[0]) return null;

        const topicCodesMap = supportTopicCodes.data[0] || {};

        return supportBots.data.map(bot => {
            if (!bot.config) {
                bot.config = {};
            }

            // Create a versions array for each bot with consistent ordering
            const versionGroup = bot.config.versionGroup;
            const botVersion = bot.config.version;
            const versions = [];

            // If there's a versionGroup, find ALL bots with the same versionGroup
            if (versionGroup !== undefined && supportBots.data) {
                // Collect all versions from bots in the same version group
                supportBots.data.forEach(otherBot => {
                    if (
                        otherBot.config &&
                        otherBot.config.versionGroup === versionGroup &&
                        !versions.includes(otherBot.config.version)
                    ) {
                        versions.push(otherBot.config.version);
                    }
                });
            } else {
                // No version group, just add this bot's version
                versions.push(botVersion);
            }

            // Sort versions to ensure DEFAULT is first
            const sortedVersions = (() => {
                const defaultVersion = versions.find(v => v === "DEFAULT");
                const otherVersions = versions.filter(v => v !== "DEFAULT");
                return defaultVersion ? [defaultVersion, ...otherVersions] : otherVersions;
            })();

            // Process topic data with version-specific overrides
            const topicData = {};
            if (bot.config.supportTopics && bot.config.supportTopics.length > 0) {
                bot.config.supportTopics.forEach(topicId => {
                    if (topicCodesMap[topicId]) {
                        // Start with the base topic data
                        const baseTopicData = { ...topicCodesMap[topicId] };

                        // Apply version-specific overrides if they exist
                        if (
                            baseTopicData.versions &&
                            botVersion &&
                            baseTopicData.versions[botVersion]
                        ) {
                            // Create a copy without the versions object
                            const processedTopic = { ...baseTopicData };

                            // Apply all overrides for this version
                            Object.entries(baseTopicData.versions[botVersion]).forEach(([key, value]) => {
                                processedTopic[key] = value;
                            });

                            // Remove the versions object from the processed topic
                            delete processedTopic.versions;

                            topicData[topicId] = processedTopic;
                        } else {
                            // No overrides for this version, use base data without versions object
                            const processedTopic = { ...baseTopicData };
                            delete processedTopic.versions;

                            topicData[topicId] = processedTopic;
                        }
                    }
                });
            }

            return {
                ...bot,
                config: {
                    ...bot.config,
                    versions: sortedVersions,
                    topicData
                }
            };
        });
    }, [supportBots.data, supportTopicCodes.data]);

    const refreshData = useCallback(() => {
        supportBots.refetch();
        supportRoles.refetch();
        supportTopicCodes.refetch();
        users.refetch();
    }, [supportBots, supportRoles, supportTopicCodes, users]);

    return {
        supportRoles,
        supportBots,
        supportTopicCodes,
        users,
        assistantInstructionsConfig,
        isLoading,
        isError,
        isSuccess,
        refreshData,
        data: {
            supportRoles: supportRoles?.data,
            supportBots: processedSupportBots || supportBots?.data,
            supportTopicCodes: supportTopicCodes?.data?.[0],
            users: users?.data,
            assistantInstructionsConfig: assistantInstructionsConfig?.data?.[0]?.value,
            supportBotInstructions: supportBotInstructionsInstructions?.data,
            supportTopicInstructionBody: supportBotInstructionsSupportTopicInstructionBody?.data,
            supportBotInstructionBody: supportBotInstructionsSupportBotInstructionBody?.data
        }
    };
}